import { useRouter } from "next/router";
import { useState, useEffect } from "react";
import Link from "next/link";
import ProductCard from "@/components/products/ProductCard";
import Button from "@/components/Button";
import { ArrowRightIcon } from "@heroicons/react/24/outline";

export default function WizardResults({
  products,
  wizardData,
  isLoading,
  onRestart,
}) {
  const router = useRouter();
  const [distanceMap, setDistanceMap] = useState({});
  const [showContent, setShowContent] = useState(false);

  // Scroll nach oben beim Laden der Results
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  // Kurze Premium-Transition (0.8s)
  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setShowContent(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  // Berechne Distanzen für regionale Boxen mit PLZ
  useEffect(() => {
    if (
      wizardData.delivery !== "regional" ||
      !wizardData.postalCode ||
      !products.length
    ) {
      return;
    }

    const calculateDistances = async () => {
      try {
        const res = await fetch("/plz-coordinates.json");
        const coordsData = await res.json();
        const userCoords = coordsData[wizardData.postalCode];

        if (!userCoords) return;

        const distances = {};
        for (const product of products) {
          const providerZip = product.selfDeliveryZip;
          if (providerZip && !distances[providerZip]) {
            const providerCoords = coordsData[providerZip];
            if (providerCoords) {
              const R = 6371;
              const dLat =
                ((providerCoords.lat - userCoords.lat) * Math.PI) / 180;
              const dLon =
                ((providerCoords.lng - userCoords.lng) * Math.PI) / 180;
              const a =
                Math.sin(dLat / 2) ** 2 +
                Math.cos((userCoords.lat * Math.PI) / 180) *
                  Math.cos((providerCoords.lat * Math.PI) / 180) *
                  Math.sin(dLon / 2) ** 2;
              const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
              distances[providerZip] = R * c;
            }
          }
        }
        setDistanceMap(distances);
      } catch (error) {
        console.error("Fehler beim Laden der PLZ-Koordinaten:", error);
      }
    };

    calculateDistances();
  }, [wizardData.postalCode, wizardData.delivery, products]);

  // Erhalte Top 3
  const topProducts = products.slice(0, 3);

  // Generiere Filter-URL für alle Produkte
  const generateFilterUrl = () => {
    const params = new URLSearchParams();

    // Event-Type Filter
    if (wizardData.eventType) {
      params.append("eventType", wizardData.eventType);
    }

    // Print-Type Filter (gleiches Schema wie im Guru)
    if (wizardData.printType) {
      params.append("printType", wizardData.printType);
    }

    // Flatrate Filter (gleiches Schema wie im Guru)
    if (wizardData.printType === "print" && wizardData.wantsFlatrate !== null) {
      params.append("wantsFlatrate", wizardData.wantsFlatrate.toString());
    }

    // Gästezahl Filter
    if (wizardData.guestCount) {
      params.append("guests", wizardData.guestCount);
    }

    // Extras Filter
    if (wizardData.extras && wizardData.extras.length > 0) {
      wizardData.extras.forEach((extra) => {
        params.append("extras", extra);
      });
    }

    // Digital Options Filter
    if (wizardData.digitalOptions && wizardData.digitalOptions.length > 0) {
      wizardData.digitalOptions.forEach((option) => {
        params.append("digitalOptions", option);
      });
    }

    // Delivery Filter (gleiches Schema wie im Guru)
    if (wizardData.delivery) {
      params.append("delivery", wizardData.delivery);
    }

    // PLZ Filter (gleiches Schema wie im Guru)
    if (wizardData.postalCode) {
      params.append("postalCode", wizardData.postalCode);
    }

    return `/fotobox-vergleich?${params.toString()}`;
  };

  const filterUrl = generateFilterUrl();

  if (isLoading || !showContent) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center space-y-4">
          <div className="inline-block animate-spin">
            <div className="h-12 w-12 border-4 border-slate-200 border-t-[#FF5744] rounded-full" />
          </div>
          <div className="space-y-2">
            <p className="text-lg font-semibold text-slate-900">
              Wir suchen die besten Fotoboxen für dich...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {/* Header */}
      <div className="text-center space-y-3 animate-[fadeIn_0.5s_ease-in]">
        <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">
          Deine persönlichen Top 3 Empfehlungen
        </h1>
        <p className="text-lg text-slate-600">
          Basierend auf deinen Anforderungen haben wir die besten Fotoboxen für
          dich ausgesucht.
        </p>
      </div>

      {/* No Results */}
      {topProducts.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <h2 className="text-xl font-bold text-slate-900 mb-3">
            Leider keine passenden Fotoboxen gefunden
          </h2>
          <p className="text-slate-600 mb-6">
            Für deine Anforderungen konnten wir momentan keine verfügbaren
            Fotoboxen finden.
            {wizardData.delivery === "regional" && wizardData.postalCode && (
              <span>
                {" "}
                Hier sind stattdessen deutschlandweite Versand-Fotoboxen für
                dich.
              </span>
            )}
          </p>
          <Button href={filterUrl} variant="primary">
            Alle verfügbaren Fotoboxen anzeigen
          </Button>
        </div>
      ) : (
        <>
          {/* Top 3 Empfehlungen */}
          <div className="grid gap-6">
            {topProducts.map((product, index) => (
              <div
                key={product.id}
                className="relative opacity-0 animate-[slideUp_0.5s_ease-out_forwards]"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                {/* Ranking Badge */}
                <div className="absolute -left-4 -top-4 w-10 h-10 bg-[#FF5744] text-white rounded-full flex items-center justify-center font-bold shadow-md z-10">
                  {index + 1}
                </div>

                {/* Product Card */}
                <ProductCard
                  item={product}
                  userPostalCode={wizardData.postalCode}
                  distance={
                    wizardData.delivery === "regional" &&
                    wizardData.postalCode &&
                    product.selfDeliveryZip
                      ? distanceMap[product.selfDeliveryZip] || null
                      : null
                  }
                />
              </div>
            ))}
          </div>

          {/* CTA: Alle Boxen zeigen */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 lg:p-12 border border-blue-100">
            <div className="max-w-2xl mx-auto text-center space-y-4">
              <h2 className="text-2xl font-bold text-slate-900">
                {products.length > 3
                  ? `Noch ${products.length - 3} weitere Optionen verfügbar`
                  : "Keine weiteren Optionen verfügbar"}
              </h2>
              <p className="text-slate-600">
                Alle Fotoboxen wurden basierend auf deinen Kriterien gefiltert.
                Du kannst in der Detailansicht noch weitere Filter anwenden.
              </p>

              <Link href={filterUrl} className="inline-block">
                <Button variant="primary" className="flex items-center gap-2">
                  Alle Fotoboxen anzeigen
                  <ArrowRightIcon className="h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </>
      )}

      {/* Restart Button */}
      <div className="text-center pt-6 border-t border-slate-200">
        <Button
          variant="secondary"
          onClick={() => {
            window.scrollTo({ top: 0, behavior: "auto" });
            onRestart();
          }}
        >
          ← Neue Suche starten
        </Button>
      </div>
    </div>
  );
}
